"use client";

import React, { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { GoogleTagManager } from "@next/third-parties/google";
import { Hotjar } from "~/ui/components/aplia/Hotjar/Hotjar";
const hotjarVersion = 6;

/** Checkout + thank-you must load GTM immediately — after Klarna/Vipps
 *  redirects users often land without a fresh click/scroll, so a
 *  interaction-gated load under-reports purchase on iPhone/Safari. */
function isConversionCriticalPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return (
    pathname === "/checkout" ||
    pathname.startsWith("/checkout/") ||
    pathname.startsWith("/order/")
  );
}

const LazyLoadGTMWithState = ({
  gtmId,
  hotjarSiteId,
}: {
  gtmId: string;
  hotjarSiteId: string;
}) => {
  const pathname = usePathname();
  const forceLoad = isConversionCriticalPath(pathname);
  const [shouldLoadGTM, setShouldLoadGTM] = useState(forceLoad);

  useEffect(() => {
    if (forceLoad) {
      setShouldLoadGTM(true);
      return;
    }

    const handleUserInteraction = () => {
      setShouldLoadGTM(true);
      window.removeEventListener("click", handleUserInteraction);
      window.removeEventListener("scroll", handleUserInteraction);
      window.removeEventListener("keydown", handleUserInteraction);
    };

    window.addEventListener("click", handleUserInteraction);
    window.addEventListener("scroll", handleUserInteraction);
    window.addEventListener("keydown", handleUserInteraction);

    return () => {
      window.removeEventListener("click", handleUserInteraction);
      window.removeEventListener("scroll", handleUserInteraction);
      window.removeEventListener("keydown", handleUserInteraction);
    };
  }, [forceLoad]);

  return shouldLoadGTM ? (
    <>
      <GoogleTagManager gtmId={gtmId} />
      <Hotjar id={hotjarSiteId} sv={hotjarVersion} />
    </>
  ) : null;
};

export default LazyLoadGTMWithState;
